import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import URL from '../../constants/urls'
import { errorMessage } from '../../constants/errorMessage'
import { Circles } from 'react-loader-spinner'

// The CardConnect iframe validates / tokenizes when one of its fields loses focus, and posts the result
// (token or validationError) a moment later. Clicking the submit button is often that blur, so the result
// arrives after the click handler ran. iframeTyping: the user is / was in the iframe since its last message.
const IFRAME_ID = 'card_number'
// Card number, expiry and CVV rows (label 20px + input 48px + 16px gap) measure about 239px; the rest is room
// for font differences (the portal's font is not loaded in the iframe) and the CVV focus shadow
const IFRAME_HEIGHT = '252px'
// Origin of the facility's CardConnect iframe; only its messages are read
const iframeOrigin = (url) => {
  try {
    return new window.URL(url, window.location.href).origin
  } catch (e) {
    return null
  }
}
let iframeTyping = false
let waiters = []
const releaseWaiters = () => {
  const pending = waiters
  waiters = []
  // Let React apply the state set by the message first
  setTimeout(() => pending.forEach((resolve) => resolve()), 50)
}

// Resolves once the iframe has posted the result of the user's last input (at most timeoutMs);
// immediately when nothing is pending. Call before reading the token / iframe error on submit.
export const waitForCardTokenizer = (timeoutMs = 3000) => {
  if (!iframeTyping) return Promise.resolve()
  return new Promise((resolve) => {
    let done = false
    const finish = () => { if (!done) { done = true; resolve() } }
    waiters.push(finish)
    setTimeout(finish, timeoutMs)
  })
}

const CardConnectTokenizer = (props) => {
  const { faciltyPaymentDetails, tokenRef } = props
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const iframeRef = useRef(null)

  // The latest props: a parent re-render (new callback functions) must not re-attach the listener, as that
  // releases a submit waiting for the token, which then reads the previous validation error
  // (e.g. "CVV is required" from before the CVV was entered)
  const latest = useRef(props)
  latest.current = props
  const iframeUrl = faciltyPaymentDetails?.fiserv_iframe_url

  useEffect(() => {
    const cardOrigin = iframeOrigin(iframeUrl)
    const handleMessage = (event) => {
      // Other scripts on the page (reCAPTCHA, wallets, ...) post messages too: never read them as a token
      if (!cardOrigin || event.origin !== cardOrigin) return
      try {
        let data
        // Handle different data types
        if (typeof event.data === 'string') {
          try {
            data = JSON.parse(event.data)
          } catch (jsonError) {
            // If it's a string but not valid JSON, treat it as a token
            if (event.data && event.data !== '[object Object]') {
              data = { message: event.data }
            } else {
              throw new Error('Invalid data format received')
            }
          }
        } else if (typeof event.data === 'object' && event.data !== null) {
          // Data is already an object
          data = event.data
        } else {
          throw new Error('Unsupported data type received')
        }
        
        if (data && data.message) {
          // Clear any previous error when successful
          
          if (latest.current.setIframeErrorMessage) {
            latest.current.setIframeErrorMessage('')
          }
          
          if (latest.current.tokenRef?.current) {
            latest.current.tokenRef.current.value = data.message
          }
          
          latest.current.setToken(data)
        } else if (data && data.validationError) {
          // Set error message for validation errors
          if(data.errorMessage === 'Invalid Account Number'){
            data.validationError = errorMessage.invalidCardNumber
          }

          if (latest.current.setIframeErrorMessage) {
            latest.current.setIframeErrorMessage(data.validationError)
          }
          throw new Error(data.validationError)
        } else if (data && data.error) {
          // Handle error property
          if (data.errorMessage === 'Invalid Account Number') {
            data.validationError = errorMessage.invalidCardNumber
          }

          if (latest.current.setIframeErrorMessage) {
            latest.current.setIframeErrorMessage(data.error)
          }
          throw new Error(data.error)
        }
      } catch (error) {
        // Set error message for any other errors
        if (latest.current.setIframeErrorMessage) {
          latest.current.setIframeErrorMessage(error.message || errorMessage.somethingWentWrong)
        }
      } finally {
        // The iframe answered; still typing if focus is still inside it (e.g. card number -> CVV)
        iframeTyping = document.activeElement === iframeRef.current
        releaseWaiters()
      }
    }

    // Focus moving into the iframe blurs this window
    const handleWindowBlur = () => {
      setTimeout(() => {
        if (document.activeElement?.id === IFRAME_ID) iframeTyping = true
      }, 0)
    }

    window.addEventListener('message', handleMessage)
    window.addEventListener('blur', handleWindowBlur)
    return () => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('blur', handleWindowBlur)
      iframeTyping = false
      releaseWaiters()
    }
  }, [iframeUrl])

  // Same look as the portal's own fields (Rubik, rounded inputs). CardConnect builds label, <br>, input for each
  // row and drops !important from this CSS. Fixed sizes on every screen width, so the content always fits
  // IFRAME_HEIGHT (a cross-origin iframe cannot report its height).
  const css = encodeURIComponent(`
      body{
        margin: 0;
        padding: 0 1px 0 0;
        font-family: Rubik, 'Segoe UI', Roboto, Arial, sans-serif;
        color: #212529;
      }
      label{
        font-size: 14px;
        line-height: 20px;
        font-weight: 500;
        color: #212529;
      }
      input{
        height: 40px;
        padding: 6px 12px;
        border: 1px solid #ccc;
        border-radius: 6px;
        margin: 8px 0 16px 0;
        box-sizing: border-box;
        font-family: Rubik, 'Segoe UI', Roboto, Arial, sans-serif;
        font-size: 14px;
        color: #212529;
        background: #fff;
        vertical-align: top;
      }
      input::placeholder{
        color: #6c757d;
      }
      input:focus{
        border-color: #26a8e0;
        outline: none;
      }
      #ccnumfield, #cccvvfield{
        width: 100%;
      }
      /* CardConnect puts a 13px inline margin on the month (right) and year (left): 2 x 13 + 18 + 2 x 23 */
      #ccexpiryfieldmonth, #ccexpiryfieldyear{
        width: calc(50% - 23px);
      }
      label[for=ccexpiryfieldyear]{
        display: inline-block;
        width: 18px;
        text-align: center;
        line-height: 56px;
        font-weight: 400;
        vertical-align: top;
      }
      #cccvvfield{
        margin-bottom: 0;
      }
`)
  const iframeSrc = `${faciltyPaymentDetails?.fiserv_iframe_url}?${URL?.FISERV_IFRAME_FORMAT_URL}&css=${css}`

  return (
    <form id="tokenform" name="tokenform" className="token-form" style={{ width: '100%', height: IFRAME_HEIGHT, position: 'relative'}}>
      <div className="token-container" width="100%">
        {!iframeLoaded ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center text-center"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: '#fff',
              zIndex: 2,
            }}
          >
            <Circles
              height="50"
              width="50"
              color="var(--bs-primary)"
              ariaLabel="circles-loading"
              visible={true}
            />
            <p className="mt-2 mb-0">Loading secure payment form…</p>
          </div>
        ) : null}
        <iframe
          ref={iframeRef}
          id={IFRAME_ID}
          name="card_number"
          src={iframeSrc}
          frameBorder="0"
          scrolling="no"
          title="Secure Card Input"
          className="token-iframe"
          style={{ width: '100%', height: IFRAME_HEIGHT, border: 'none', marginTop: 0, visibility: iframeLoaded ? 'visible' : 'hidden' }}
          onLoad={() => setIframeLoaded(true)}
        ></iframe>
      </div>

      {/* Hidden input for token */}
      <input type="hidden" id="mytoken" name="mytoken" ref={tokenRef} />
    </form>
  )
}

CardConnectTokenizer.propTypes = {
  dispatch: PropTypes.func,
  loader: PropTypes.bool,
  onSubmit: noop,
  isSubmittingForm: PropTypes.bool,
  setToken: PropTypes.func,
  faciltyPaymentDetails: PropTypes.object,
  setIframeErrorMessage: PropTypes.func,
  tokenRef: PropTypes.object
}

export default CardConnectTokenizer
